import {
  keepPreviousData,
  queryOptions,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query'

import { livePolling } from '@/app/query-client'
import { floorsheetKeys, floorsheetTradesQuery } from '@/features/floorsheet/api/keys'
import {
  FLOORSHEET_PAST_DAY_PANELS_AVAILABLE,
  floorsheetService,
  type FloorsheetDay,
} from '@/features/floorsheet/api/floorsheetService'
import { useMarketStatus } from '@/hooks/useMarketStatus'
import type {
  FloorsheetBrokerOption,
  FloorsheetInsights,
  FloorsheetQuery,
  FloorsheetSummary,
  TradeRow,
} from '@/schemas/floorsheet'
import type { TablePage } from '@/schemas/table'

/**
 * "Today across NEPSE" — the four figures above the table, for the day the date button names.
 * Part of the key, so stepping back a day and forward again is a cache hit.
 */
export const floorsheetSummaryQueryOptions = (day: FloorsheetDay) =>
  queryOptions<FloorsheetSummary>({
    queryKey: floorsheetKeys.summary(day),
    queryFn: () => floorsheetService.summary(day),
  })

/**
 * `keepPreviousData`: stepping between days must not blank the panel while the next one loads —
 * the member is reading the previous day's figures, not nothing.
 */
export const useFloorsheetSummary = (day: FloorsheetDay): UseQueryResult<FloorsheetSummary> =>
  useQuery<FloorsheetSummary>({
    ...floorsheetSummaryQueryOptions(day),
    placeholderData: keepPreviousData,
  })

/** The trade log, SERVER-PAGED: the service answers with the envelope the pager reads. */
export const floorsheetTradesQueryOptions = (query: FloorsheetQuery, day: FloorsheetDay) =>
  queryOptions<TablePage<TradeRow>>({
    queryKey: floorsheetKeys.trades(floorsheetTradesQuery(query, day)),
    queryFn: () => floorsheetService.trades(floorsheetTradesQuery(query, day)),
  })

/**
 * Polls while the market is open — today's log grows at the head with every execution — and only
 * for today: a past day's record is settled. `keepPreviousData` holds the page a member was
 * reading, dimmed, through a page turn, a filter or a re-sort.
 */
export const useFloorsheetTrades = (
  query: FloorsheetQuery,
  day: FloorsheetDay,
): UseQueryResult<TablePage<TradeRow>> => {
  const { isOpen } = useMarketStatus()

  return useQuery<TablePage<TradeRow>>({
    ...floorsheetTradesQueryOptions(query, day),
    placeholderData: keepPreviousData,
    refetchInterval: livePolling(isOpen && day.kind === 'today'),
  })
}

export const floorsheetBrokerOptionsQueryOptions = () =>
  queryOptions<FloorsheetBrokerOption[]>({
    queryKey: floorsheetKeys.brokerOptions(),
    queryFn: () => floorsheetService.brokerOptions(),
  })

/** The Buyer and Seller menus' rows. */
export const useFloorsheetBrokerOptions = (): UseQueryResult<FloorsheetBrokerOption[]> =>
  useQuery(floorsheetBrokerOptionsQueryOptions())

export const floorsheetInsightsQueryOptions = (day: FloorsheetDay) =>
  queryOptions<FloorsheetInsights>({
    queryKey: floorsheetKeys.insights(day),
    queryFn: () => floorsheetService.insights(day),
  })

/**
 * The two cards under the table. Today's only: on a past day nothing is requested while
 * `FLOORSHEET_PAST_DAY_PANELS_AVAILABLE` is false, and the cards say so.
 */
export const useFloorsheetInsights = (day: FloorsheetDay): UseQueryResult<FloorsheetInsights> =>
  useQuery({
    ...floorsheetInsightsQueryOptions(day),
    enabled: day.kind === 'today' || FLOORSHEET_PAST_DAY_PANELS_AVAILABLE,
  })
